import type { CapabilityBinding, ModuleMeta } from '../../core/types';
import { getCapability } from './registry';

export interface PluginLayoutInput<TField extends string> {
  fieldKeys: readonly TField[];
  modules: readonly ModuleMeta<TField>[];
  capabilities: readonly CapabilityBinding[];
}

export interface PluginLayoutIssue {
  path: string;
  message: string;
}

/** 校验 fields / modules / capabilities 三份配置一致性 */
export function validatePluginLayout<TField extends string>(layout: PluginLayoutInput<TField>): PluginLayoutIssue[] {
  const issues: PluginLayoutIssue[] = [];
  const fieldSet = new Set(layout.fieldKeys);
  const capabilityKeys = new Set(layout.capabilities.map((item) => item.key));

  for (const binding of layout.capabilities) {
    const bindingKey = String(binding.key);
    if (!getCapability(bindingKey)) {
      issues.push({
        path: `capabilities.${bindingKey}`,
        message: `Capability "${bindingKey}" is not registered`,
      });
    }
  }

  for (const mod of layout.modules) {
    for (const field of mod.fields) {
      if (!fieldSet.has(field)) {
        issues.push({
          path: `modules.${mod.key}.fields.${field}`,
          message: `Field "${field}" is not declared in fieldKeys`,
        });
      }
    }

    for (const binding of mod.capabilities) {
      if (!capabilityKeys.has(String(binding.key))) {
        issues.push({
          path: `modules.${mod.key}.capabilities.${String(binding.key)}`,
          message: `Capability binding "${String(binding.key)}" is missing from capabilities.ts`,
        });
      }
    }
  }

  const assignedFields = new Map<string, string>();
  for (const mod of layout.modules) {
    for (const binding of mod.capabilities) {
      const bindingKey = String(binding.key);
      const def = getCapability(bindingKey);
      if (!def) {
        continue;
      }
      for (const field of def.fields) {
        const owner = assignedFields.get(field);
        if (owner && owner !== bindingKey) {
          issues.push({
            path: `capabilities.${bindingKey}.fields.${field}`,
            message: `Field "${field}" is already owned by capability "${owner}"`,
          });
        } else {
          assignedFields.set(field, bindingKey);
        }
      }
    }
  }

  return issues;
}

export function assertPluginLayout<TField extends string>(layout: PluginLayoutInput<TField>): void {
  const issues = validatePluginLayout(layout);
  if (issues.length > 0) {
    const detail = issues.map((item) => `${item.path}: ${item.message}`).join('\n');
    throw new Error(`Invalid plugin layout:\n${detail}`);
  }
}
